/* External */
import {
  memo, useMemo, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';

/* Components */
import ScrollIntoViewIfFocused from '../ScrollIntoViewIfFocused';
import Card2x3 from '../Card2x3';

/* Utils */
import { redirectToDetail } from '../../utils/history';
import { noOp } from '../../utils/functions';
import { Direction } from '../../utils/spatialNavigation';
import { useSubtitleCatalogCard } from './utils';
import { isSports, useTitle } from '../../utils/metaDataUtils';
import { Content, Show } from '../../storage/models';
import useLockIcon from '../../hooks/useLockIcon';
import parentalControlBlock from '../../utils/parentalControlUtils';

import {
  formatContentData,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
  playerType,
} from '../../utils/googleTags';

function CatalogCard({
  content,
  width,
  blockScroll,
  position,
  handleArrowPress,
  setFocus,
  index,
  focusFirstCard,
  isLastCard,
  hideMetadata,
  dark,
  locationState,
  pdpCommonTagEvent,
  isShow,
  myListCommonTagEvent,
  isMyList,
}) {
  const {
    images,
  } = content || {};

  const isNonEntitledContent = useLockIcon(content);
  const isContentBlockedByParentalControl = parentalControlBlock(content);

  useEffect(() => {
    if (focusFirstCard && position === 0) {
      setFocus();
    }
  }, [focusFirstCard, position, setFocus]);

  const title = useTitle(content);
  const subtitle = useSubtitleCatalogCard(content);

  const style = useMemo(() => ({
    width,
  }), [width]);

  const catalogVodCardHandleArrowPress = useCallback((direction, props) => {
    if (direction === Direction.RIGHT && isLastCard) {
      return false;
    }

    handleArrowPress(direction, props);
    return true;
  }, [handleArrowPress, isLastCard]);

  const handlePdpCommonTagEvent = useCallback(() => {
    if (!isMyList) {
      pdpCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PDP.WATCH_CONTENT,
        subcategory: isShow
          ? GTM_SUBCATEGORIES.PDP.SERIE_SEASONS_AND_RELATED_CONTENT
          : GTM_SUBCATEGORIES.PDP.MOVIES_RELATED_CONTENT,
      });
    }
  }, [isMyList, isShow, pdpCommonTagEvent]);

  const handleMyListCommonTagEvent = useCallback(() => {
    if (isMyList) {
      myListCommonTagEvent({
        eventName: GTM_EVENT_NAMES.MY_LIST.WATCH,
        data: {
          content: {
            ...formatContentData({
              content,
              rest: {
                playSource: locationState({ content })?.gtmPlaySource,
                isLive: false,
              },
            }),
          },
          ...locationState({ content }),
          listPosition: index + 1,
          playerType: playerType(content?.type || content?.schema, false, isSports(content)),
          isKids: false,
        },
      });
    }
  }, [content, index, isMyList, locationState, myListCommonTagEvent]);

  const handleEnterPress = useCallback(() => {
    redirectToDetail(content, { state: locationState({ content }) });
    handlePdpCommonTagEvent();
    handleMyListCommonTagEvent();
  }, [content, locationState, handleMyListCommonTagEvent, handlePdpCommonTagEvent]);

  return (
    <ScrollIntoViewIfFocused
      className="dtv-html5-c-catalog-vod__card"
      position={blockScroll}
      style={style}
    >
      <Card2x3
        index={index}
        images={images}
        title={title}
        subtitle={subtitle}
        isNonEntitledContent={isNonEntitledContent}
        isContentBlockedByParentalControl={isContentBlockedByParentalControl}
        isLastCard={isLastCard}
        onArrowPress={catalogVodCardHandleArrowPress}
        onEnterPress={handleEnterPress}
        hideMetadata={hideMetadata}
        dark={dark}
        content={content}
      />
    </ScrollIntoViewIfFocused>
  );
}

CatalogCard.propTypes = {
  index: PropTypes.number,
  focusFirstCard: PropTypes.bool,
  isLastCard: PropTypes.bool,
  hideMetadata: PropTypes.bool,
  dark: PropTypes.bool,
  content: PropTypes.oneOfType([
    PropTypes.instanceOf(Content),
    PropTypes.instanceOf(Show),
  ]),
  blockScroll: PropTypes.oneOf([
    'center',
    'end',
    'nearest',
    'start',
  ]).isRequired,
  width: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  handleArrowPress: PropTypes.func,
  setFocus: PropTypes.func.isRequired,
  locationState: PropTypes.instanceOf(Object),
  pdpCommonTagEvent: PropTypes.func,
  isShow: PropTypes.bool,
  myListCommonTagEvent: PropTypes.func,
  isMyList: PropTypes.bool,
};

CatalogCard.defaultProps = {
  content: undefined,
  handleArrowPress: noOp,
  index: 0,
  focusFirstCard: false,
  isLastCard: false,
  hideMetadata: false,
  dark: false,
  locationState: {},
  pdpCommonTagEvent: noOp,
  isShow: false,
  myListCommonTagEvent: noOp,
  isMyList: false,
};

export default memo(withFocusable()(memo(CatalogCard)));
